import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, User, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlagEN, FlagNL } from '../ui/Flag';

const MENU_PANE = {
    closed: { opacity: 0, y: -10, scaleY: 0.85 },
    open: { opacity: 1, y: 0, scaleY: 1, transition: { type: 'spring', stiffness: 380, damping: 30 } },
};


export function Navbar() {
    const { t, lang, toggleLang } = useLanguage();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Hide the pre-order CTA on educational pages (Science) to keep the two worlds separate
    const hideCta = location.pathname.startsWith('/science');

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handler);
        return () => window.removeEventListener('scroll', handler);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const navLinks = [
        { href: '/#product', label: t('nav.product') },
        { href: '/science', label: t('nav.science') },
        { href: '/business', label: t('nav.business') },
        { href: '/blog', label: t('nav.blog') },
        { href: '/about', label: t('nav.about') },
        { href: '/faq', label: t('nav.faq') },
    ];

    return (
        <header
            className={`fixed left-0 right-0 z-40 transition-colors duration-300 ${
                scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border' : 'bg-transparent'
            }`}
            style={{ top: 'var(--banner-h, 56px)' }}
        >
            <div className="section-container">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" data-testid="nav-logo">
                        <img
                            src="/logo-mark.png"
                            alt="Sleepstack"
                            className="h-7 md:h-8 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-6">
                        {navLinks.map(link => (
                            <a
                                key={link.href}
                                href={link.href}
                                className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200"
                            >
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    {/* Right side */}
                    <div className="hidden lg:flex items-center gap-3">
                        {/* Language toggle */}
                        <button
                            onClick={toggleLang}
                            data-testid="lang-toggle"
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-primary border border-border rounded-full px-3 py-1.5 transition-colors duration-200"
                            aria-label={lang === 'en' ? 'Switch to Dutch' : 'Switch to English'}
                        >
                            {lang === 'en' ? <FlagNL size={14} /> : <FlagEN size={14} />}
                            {lang === 'en' ? 'NL' : 'EN'}
                        </button>

                        {/* Account */}
                        {user ? (
                            <div className="flex items-center gap-2">
                                <Link
                                    to="/dashboard"
                                    data-testid="nav-dashboard"
                                    className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-primary transition-colors"
                                >
                                    <User size={16} />
                                    {t('nav.dashboard')}
                                </Link>
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="nav-login"
                                className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
                            >
                                {t('nav.login')}
                            </Link>
                        )}

                        {/* CTA */}
                        {/* CTA */}
                        {!hideCta && (
                            <Link
                                to="/checkout"
                                data-testid="nav-cta"
                                className="btn-primary text-sm py-2.5 px-6"
                            >
                                {t('nav.preorder')}
                            </Link>
                        )}
                    </div>

                    {/* Mobile: lang toggle + hamburger */}
                    <div className="lg:hidden flex items-center gap-2">
                        <button
                            onClick={toggleLang}
                            data-testid="lang-toggle-mobile"
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-primary border border-border rounded-full px-3 py-1.5 transition-colors duration-200"
                            aria-label="Toggle language"
                        >
                            {lang === 'en' ? <FlagNL size={14} /> : <FlagEN size={14} />}
                            {lang === 'en' ? 'NL' : 'EN'}
                        </button>
                        <button
                            className="p-2 text-slate-700"
                            onClick={() => setMobileOpen(!mobileOpen)}
                            data-testid="mobile-menu-btn"
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu, full-screen overlay rendered in a portal so the
                header's backdrop-filter can't create a containing block for it */}
            {createPortal(
                <AnimatePresence>
                    {mobileOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, transition: { duration: 0.2, delay: 0.1 } }}
                            transition={{ duration: 0.2 }}
                            className="lg:hidden fixed left-0 right-0 bottom-0 z-50 bg-white/30 backdrop-blur-md overflow-y-auto"
                            style={{ top: 'calc(var(--banner-h, 56px) + 64px)' }}
                            onClick={(e) => e.target === e.currentTarget && setMobileOpen(false)}
                            data-testid="mobile-menu-overlay"
                        >
                            <motion.div
                                className="section-container py-4 flex flex-col gap-2"
                                initial="closed"
                                animate="open"
                                exit="closed"
                                variants={{ open: { transition: { staggerChildren: 0.05 } }, closed: { transition: { staggerChildren: 0.03, staggerDirection: -1 } } }}
                            >
                                {[
                                    ...navLinks.map((link) => ({ key: link.href, href: link.href, label: link.label, cls: 'text-slate-800' })),
                                    user
                                        ? { key: 'dash', to: '/dashboard', label: t('nav.dashboard'), cls: 'text-primary' }
                                        : { key: 'login', to: '/login', label: t('nav.login'), cls: 'text-primary' },
                                ].map((item) => {
                                    const Tag = item.to ? Link : 'a';
                                    const linkProps = item.to ? { to: item.to } : { href: item.href };
                                    return (
                                        <motion.div key={item.key} variants={MENU_PANE} style={{ transformOrigin: 'top center' }}>
                                            <Tag
                                                {...linkProps}
                                                className={`block px-5 py-3.5 text-lg font-medium rounded-2xl bg-white/45 backdrop-blur-2xl backdrop-saturate-150 border border-white/70 shadow-[0_6px_24px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.7)] ${item.cls}`}
                                                onClick={() => setMobileOpen(false)}
                                                data-testid={`mobile-menu-link-${item.key.replace(/[^a-z]/gi, '') || 'home'}`}
                                            >
                                                {item.label}
                                            </Tag>
                                        </motion.div>
                                    );
                                })}
                                <motion.div variants={MENU_PANE} style={{ transformOrigin: 'top center' }} className={`mt-2 p-3 rounded-2xl bg-white/45 backdrop-blur-2xl backdrop-saturate-150 border border-white/70 shadow-[0_6px_24px_rgba(15,23,42,0.08)] ${hideCta ? 'hidden' : ''}`}>
                                    <Link
                                        to="/checkout"
                                        className="btn-primary block text-center py-4"
                                        onClick={() => setMobileOpen(false)}
                                        data-testid="mobile-cta"
                                    >
                                        {t('nav.preorder')}
                                    </Link>
                                    <p className="text-center text-xs text-muted-foreground mt-2">{t('banner.ships_value')}</p>
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </header>
    );
}
